import {FunctionalComponent, h, Fragment, ComponentChild} from "preact";
import {useEffect, useState} from "preact/hooks";
import {legacyRender, metaDataRender, preLoader} from "./MediaBoxService";
import {Loader} from "../../components/loader";
import {IcoExtLink} from "../../components/icons";
import {log} from "../../libs/QuerloLogger";
import {MessageFrame} from "../../components/messageFrame";

const MediaBox: FunctionalComponent<{ msgID: string; extLink: string; desc: string; rawImageObj: any; metaData: any; assetsBaseURL: string; updateViewportCallback: () => void; tickCallback: () => void; lightboxCallback: (content: ComponentChild) => void }> =
    ({msgID, extLink, desc, rawImageObj, metaData, assetsBaseURL, updateViewportCallback, tickCallback, lightboxCallback}) => {

      let renderData: any;

      if (metaData) {
          renderData = metaDataRender(metaData, assetsBaseURL);
      } else {
          renderData = legacyRender(rawImageObj, assetsBaseURL);
      }

      let cn = renderData.video ? 'video' : 'img';
      if (renderData.isTenor) cn += ' tenor';

      const [imgURL, setImgURL] = useState<string>('');
        const [imgLoading, setImgLoading] = useState<boolean>(!renderData.isVideo);

        function onVideoLoadedData(e: Event): void {
            updateViewportCallback();
            (e.currentTarget as HTMLVideoElement).muted = true;
        }

        useEffect(() => {
            // log.info(`use effect for ${msgText}`);
            if (!renderData.isVideo) {
                log.debug(`Preloading started for img ${renderData.img}`);
                preLoader(renderData.img).then(() => {
                    log.debug(`Preloading complete for img ${renderData.img}`);
                    setImgLoading(false);
                    setImgURL(renderData.img);
                    /* preloader won't work when browser cache is disabled (usually when devtools are opened) */
                    tickCallback();
                }, () => {
                    tickCallback();
                });
            } else {
                tickCallback();
            }
        }, [msgID]);

        useEffect(() => {
            /** we skip initial call on mounting, when imgURL is empty */
            if (!imgURL) {
                return;
            }

            /** here image has been loaded, preLoader callback has rendered it by toggling imgLoading flag and
             * setting img src attribute in mediaBox, so mediaBox node has changed (increased) its size
             * and we must call updateViewport to adjust scroll if needed */

            log.trace(`MediaBox UseEffect for imgURL ${imgURL}`);
            updateViewportCallback();
        }, [imgURL]);

        let content;
        let pexelsAuthorEl;

        if (renderData.isPexels) {
          pexelsAuthorEl = <Fragment>
            <a className="imgCredit"
               rel="noreferrer"
               href="https://www.pexels.com"
               target="_blank">
              {renderData.author} <img src="https://images.pexels.com/lib/api/pexels.png" />
            </a>
          </Fragment>;
        }

        const lightboxImgEl = <div className="wrp">
          <img src={imgURL} className="img" />
        </div>;

        const lightboxData = {
          imgURL,
          author: renderData.author,
          node: renderData.isPexels ?
            <Fragment>
              {lightboxImgEl}
              {pexelsAuthorEl}
            </Fragment>
            :
            {lightboxImgEl}
        };

        if (renderData.video) {
          const videoEl = <video onLoadedData={onVideoLoadedData}autoPlay controls loop muted>
            <source src={renderData.video} type={`video/${renderData.ext}`} />
          </video>;

          content = <Fragment>
              <div className="imgWrp">
                  {extLink && (<a href={extLink} target="_blank" className="imgLink" rel="noreferrer">
                    {videoEl}
                      <span className="extLink butt">
                        <IcoExtLink />
                      </span>
                  </a>)}
                  {!extLink && videoEl }
              </div>
          </Fragment>
        } else {
          /* TODO support back this:
              <img ng-if="::isUpload" rt-src="{{::img}}"/>
              <img ng-if="::!isUpload" src="{{::img}}"/>
         */
          content = <Fragment>
              {imgLoading && <div className="loader"><Loader /></div>}
              {!imgLoading && !renderData.isVideo && imgURL &&
              (<Fragment>
                {!extLink && (<div className="imgWrp" onClick={(): void => lightboxCallback(lightboxData)}>
                  <img src={imgURL} />
                  {renderData.isPexels ? pexelsAuthorEl : '' }
                </div>)}

                {extLink && (<a href={extLink} target="_blank" className="imgLink imgWrp" rel="noreferrer">
                  <img src={imgURL} />
                  <span className="extLink butt"><IcoExtLink /></span>
                  {renderData.isPexels ? pexelsAuthorEl : '' }
                </a>)}
              </Fragment>)}
          </Fragment>
        }

        return (<MessageFrame clsName={cn}>
            {content}
            {desc && <p className="imgCaption">{desc}</p>}
        </MessageFrame>);
    };

export default MediaBox;
